import { LINE_SYMBOL_GROUP_LABEL, LINE_SYMBOL_GROUP_ORDER, lineTypeGroup, resolveLineSymbol } from '@ikegps/core-api';
import type { FormModelFieldLine, FormModelFieldLineOption } from '@ikegps/core-shared';
import { observable } from 'mobx';
import { ModalService } from '../../components/modal/modal.service';
import { LineSwatch } from '../../components/line.swatch';
import { SearchInput } from '../../components/search.input/search.input';
import { addStyles, DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';

const CSS = {
	container: 'c-LineTypeModal',
	search: 'c-LineTypeModal__search',
	list: 'c-LineTypeModal__list',
	group: 'c-LineTypeModal__group',
	row: 'c-LineTypeModal__row',
	selected: 'c-LineTypeModal__row--selected',
	name: 'c-LineTypeModal__name',
	empty: 'c-LineTypeModal__empty',
};

addStyles({
	[CSS.container]: { width: '360px', 'max-width': '100%', display: 'flex', 'flex-direction': 'column' },
	[CSS.search]: { 'margin-bottom': DefaultTheme.space(1) },
	[CSS.list]: { 'max-height': '320px', 'overflow-y': 'auto' },
	[CSS.group]: {
		'font-weight': 'bold',
		color: DefaultTheme.font.color.supporting,
		padding: `${DefaultTheme.space(1)} ${DefaultTheme.space(1)} 4px`,
	},
	[CSS.row]: {
		display: 'flex',
		'align-items': 'center',
		gap: '10px',
		padding: '6px 10px',
		cursor: 'pointer',
		'border-left': '3px solid transparent',
	},
	[CSS.selected]: { 'border-left': '3px solid #f5871f', background: '#f6f7f8' },
	[CSS.name]: { flex: '1 1 auto' },
	[CSS.empty]: { padding: DefaultTheme.space(2), color: DefaultTheme.font.color.supporting },
});

interface PickerState {
	selected: string;
	search: string;
}

/** Open the "Select Line Type" picker. Resolves to the chosen type's title, or null on cancel. */
export async function openLineTypeModal(schema: FormModelFieldLine, current: string): Promise<string | null> {
	// Observable so row clicks / search input trigger a reactive re-render of the modal content.
	const state: PickerState = observable({ selected: current, search: '' });
	const result = await ModalService.open({
		title: T.LINE_TYPE_MODAL_TITLE,
		hasCancel: true,
		okName: T.OK,
		// Required contract: a line type must be chosen to confirm.
		okDisabled: () => state.selected === '',
		content: () => viewPicker(schema, state),
	});
	return result.ok ? state.selected : null;
}

function viewPicker(schema: FormModelFieldLine, state: PickerState): MithrilVNode {
	const search = state.search.trim().toLowerCase();
	const options = schema.visibleLineTypes.filter(
		(option) => search === '' || option.title.toLowerCase().includes(search)
	);

	const groups: MithrilVNode[] = [];
	for (const group of LINE_SYMBOL_GROUP_ORDER) {
		const inGroup = options.filter((option) => lineTypeGroup(option.title) === group);
		if (inGroup.length === 0) {
			continue;
		}
		groups.push(m('div', { className: CSS.group }, Translate(LINE_SYMBOL_GROUP_LABEL[group])));
		for (const option of inGroup) {
			groups.push(viewRow(option, state));
		}
	}

	return m('div', { className: CSS.container }, [
		SearchInput.view({
			className: CSS.search,
			placeholder: Translate(T.LINE_TYPE_SEARCH),
			value: state.search,
			onValue: (next) => {
				state.search = next;
			},
		}),
		groups.length > 0
			? m('div', { className: CSS.list }, groups)
			: m('div', { className: CSS.empty }, Translate(T.LINE_TYPE_NO_RESULTS)),
	]);
}

function viewRow(option: FormModelFieldLineOption, state: PickerState): MithrilVNode {
	const symbol = resolveLineSymbol(option);
	const isSelected = state.selected === option.title;
	return m(
		'div',
		{
			className: isSelected ? `${CSS.row} ${CSS.selected}` : CSS.row,
			onclick: () => {
				state.selected = option.title;
			},
		},
		[LineSwatch.view({ symbol }), m('div', { className: CSS.name }, option.title)]
	);
}
